import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    NgModule,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { IFilterItemViewModel, IImage, ILayoutSettings } from '@ncg/data';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { SwiperModule } from 'swiper/angular';

import { SettingsService } from '../../core/settings.service';
import { FavoritesServiceModule } from '../../favorites-service/favorites-service.module';
import { FilterService } from '../../filter/filter.service';
import { productListAnimation } from '../../utils/animations/filter.animation';
import { UtilsModule } from '../../utils/utils.module';
import { ProductItemCarouselComponent } from '../product-item-carousel/product-item-carousel.component';
import { ProductItemContentComponent } from '../product-item/product-item-content.component';
import { ProductItemPriceComponent } from '../product-item/product-item-price.component';
import { ProductItemTagsComponent } from '../product-item/product-item-tags.component';
import { ProductItemComponent } from '../product-item/product-item.component';
import { ProductSkeletonComponent } from '../product-skeleton/product-skeleton.component';
import { ProductService } from '../product.service';

export interface ProductListStates {
    noItemsFound: boolean;
    isFetching: boolean;
}

export interface ProductListStateChange {
    key: keyof ProductListStates;
    value: boolean;
}

@Component({
    template: `
        <div class="product-list">
            <div class="columns is-multiline" [@listAnimation]="items?.length">
                <ncg-product-item
                    *ngFor="let item of items; trackBy: trackByMethod"
                    [item]="item"
                    [fallbackImage]="fallbackImage"
                    [layout]="layout"
                    class="product-list__item column is-full-mobile"
                    [ngClass]="{
                        'is-half': layout?.view === 'grid' && layout?.pageLayout !== 'filterVisible',
                        'is-half-tablet is-one-third-fullwidth': layout?.view === 'grid' && layout?.pageLayout === 'filterVisible',
                        'is-full': layout?.view === 'list'
                    }"
                ></ncg-product-item>

                <ng-container *ngIf="states.noItemsFound && !states.isFetching && !shouldGetItems">
                    <div class="column is-full has-text-centered">
                        <div class="product-list__empty">
                            <h2 class="product-list__empty-title">{{ 'product_list.no_results' | translate }}</h2>
                            <button
                                *ngIf="layout?.pageLayout !== 'filterVisible'"
                                type="button"
                                class="button is-primary"
                                (click)="openFilterPanel()"
                                aria-label="Open filter panel"
                            >
                                {{ 'product_list.no_result_button_label' | translate }}
                            </button>
                        </div>
                    </div>
                </ng-container>

                <ng-container *ngIf="!states.noItemsFound && !items?.length">
                    <ncg-product-skeleton
                        aria-hidden="true"
                        *ngFor="let skeleton of skeletons; trackBy: trackByMethod"
                        class="column is-12-mobile"
                        [ngClass]="{
                            'is-6': layout?.view === 'grid' && layout?.pageLayout !== 'filterVisible',
                            'is-6-tablet is-4-fullhd': layout?.view === 'grid' && layout?.pageLayout === 'filterVisible',
                            'is-full': layout?.view === 'list'
                        }"
                        [view]="layout?.view"
                    ></ncg-product-skeleton>
                </ng-container>
            </div>
        </div>
    `,
    animations: [productListAnimation],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListComponent implements OnInit, OnChanges, OnDestroy {
    @Input() items: IFilterItemViewModel[] = [];
    @Input() layout?: ILayoutSettings;
    @Input() fromAmount = 0;
    @Input() takeAmount = 2;
    @Input() filterQuery: { [key: string]: any } = {};
    @Input() readonly states: ProductListStates = {
        noItemsFound: false,
        isFetching: false,
    };
    @Input() shouldGetItems = false;
    @Input() skeletonsAmount = 6;
    @Output() stateChanged = new EventEmitter<ProductListStateChange>();
    fallbackImage?: IImage;
    get skeletons() {
        const arr: number[] = [];
        for (let i = 1; i < this.skeletonsAmount + 1; i++) {
            arr.push(i);
        }
        return arr;
    }

    private readonly unsubscribe = new Subject<void>();

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly productService: ProductService,
        private readonly filterService: FilterService,
        private readonly settingsService: SettingsService
    ) {}

    ngOnInit() {
        if (this.shouldGetItems) {
            this.getItems();
        }

        this.setFallbackImage();
    }

    ngOnChanges() {
        this.cd.markForCheck();
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    setFallbackImage() {
        this.settingsService
            .get()
            .pipe(take(1), takeUntil(this.unsubscribe))
            .subscribe((settings) => {
                this.fallbackImage = settings.productFallbackImage;
                this.cd.markForCheck();
            });
    }

    getItems() {
        if (!this.shouldGetItems && !Object.keys(this.filterQuery).length) {
            this.changeState('noItemsFound', true);
            this.cd.markForCheck();
            return;
        }

        this.productService
            .filter(
                {
                    from: this.fromAmount.toString(),
                    take: this.takeAmount.toString(),
                    ...this.filterQuery,
                },
                false
            )
            .pipe(
                finalize(() => {
                    this.cd.markForCheck();
                }),
                takeUntil(this.unsubscribe)
            )
            .subscribe((data) => {
                if (data.hits?.length) {
                    this.items.length = 0;
                    this.items = this.items.concat(data.hits);
                    this.changeState('noItemsFound', false);
                } else {
                    this.changeState('noItemsFound', true);
                }
                this.cd.markForCheck();
            });
    }

    openFilterPanel() {
        this.filterService.openFilterPanel();
    }

    trackByMethod(index: number, item: any) {
        return item.id || index;
    }

    changeState(key: keyof ProductListStates, value: boolean): void {
        this.states[key] = value;
        this.stateChanged.emit({ key, value });
    }
}
@NgModule({
    declarations: [ProductListComponent, ProductItemComponent, ProductSkeletonComponent, ProductItemCarouselComponent, ProductItemContentComponent],
    imports: [
        CommonModule,
        TranslateModule.forChild(),
        RouterModule,
        SwiperModule,
        UtilsModule,
        FavoritesServiceModule,
        ProductItemTagsComponent,
        ProductItemPriceComponent,
    ],
})
export class ProductListComponentModule {}
